/**
 * CustomerBookingsPage — `/dashboard/customer/bookings`.
 *
 * Phase 2 extraction from `CustomerDashboard.jsx`'s right-rail
 * bookings cards. Self-contained: fetches its own upcoming + past
 * bookings via `/bookings/mine`. Preserves all prior data-testids
 * (`customer-upcoming-bookings`, `customer-past-bookings`,
 * `booking-upcoming-{id}`, `booking-past-{id}`, etc.) so existing
 * tests survive the migration.
 *
 * Rate My Pluck — single-source-of-truth (Item E migration +
 * E-follow-up rating denormalization):
 *   - Reads the server-side `has_review` flag attached to each row
 *     by `GET /api/bookings/mine` (added in Item #6). Same flag the
 *     dashboard `RateMyPluckBanner` reads — they can never disagree
 *     for the same booking, pinned by
 *     `RateMyPluckBanner.test.jsx::"BOTH read has_review from the
 *     SAME /bookings/mine payload"`.
 *   - Reads `last_review_rating` from the same row (added in the
 *     Item E-follow-up backend extension) to drive the inline 1-5
 *     stars in the reviewed pill. ZERO extra round-trips — same
 *     `$in` lookup that derives `has_review` also carries the
 *     rating. The pre-Item-E `<StarRating value={review.rating} />`
 *     visual is restored byte-identical.
 *   - Legacy parallel `/reviews/mine` fetch + client-side join
 *     (`reviewsByBooking` map) is GONE — drift bait killed.
 *   - Completed bookings WITHOUT a review render the "Rate My Pluck"
 *     CTA (slate `#2B3942` on orange `#F89623` per the locked
 *     in-app CTA brand rule).
 *   - Completed bookings WITH a review render a "✓ Reviewed · ★…"
 *     pill — stars sourced from `b.last_review_rating`.
 *   - After the customer submits a review through the in-page
 *     ReviewModal we refetch `/bookings/mine` so both
 *     `has_review` AND `last_review_rating` update server-side —
 *     no parallel local-state mirror.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useStripeConfig } from "@/context/StripeConfigContext";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";
import {
  BookingStatusPill,
  PaymentBadge,
  PriceBreakdown,
  RecurringTag,
} from "@/components/booking-bits";
import StarRating from "@/components/reviews/StarRating";
import ReviewModal from "@/components/reviews/ReviewModal";
import {
  CalendarClock,
  ChevronDown,
  ChevronUp,
  History,
  PawPrint,
  Plus,
  Star,
} from "lucide-react";

function formatServiceDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ReviewedPill({ bookingId, rating }) {
  // Item E-follow-up — inline star count restored from the
  // server-side `last_review_rating` denormalization on the
  // bookings/mine row. SAME testid + position + copy as the
  // pre-Item-E pill. `rating` falsy guards against a row that
  // somehow has `has_review=true` but no rating (shouldn't happen
  // server-side, but the fallback avoids passing `undefined` to
  // StarRating).
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-pp-orange/15 px-3 py-1 text-xs font-bold text-pp-text"
      data-testid={`reviewed-pill-${bookingId}`}
    >
      <span className="text-pp-text">✓ Reviewed ·</span>
      {rating ? (
        <StarRating
          value={rating}
          readOnly
          size="sm"
          testidPrefix={`reviewed-pill-${bookingId}-stars`}
        />
      ) : null}
    </span>
  );
}

function PastBookingCard({ b, expanded, onToggle, onRate }) {
  const paid = b.payment_status === "paid";
  const cancelled = b.status === "cancelled";
  const completed = b.status === "completed";
  // Item E — single source of truth: the server-side `has_review`
  // flag. Same field the dashboard RateMyPluckBanner reads, so the
  // two surfaces can never disagree on review state for the same
  // booking. `=== true` so missing/undefined never silently flips to
  // "reviewed" — only the server's explicit confirmation counts.
  const hasReview = b.has_review === true;
  const canReview = completed && !hasReview;

  return (
    <div
      className="rounded-2xl border border-border bg-pp-surface p-4"
      data-testid={`booking-past-${b.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-pp-text">
            {formatServiceDate(b.service_date)}
          </p>
          <p className="text-sm text-muted-foreground">
            {b.provider_business_name || "Provider"}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <PaymentBadge paid={paid} />
            {cancelled && <BookingStatusPill status="cancelled" />}
            {hasReview && (
              <ReviewedPill
                bookingId={b.id}
                rating={b.last_review_rating}
              />
            )}
          </div>
          {/* Phase 3.3 G3 — refund status pill on PAST bookings.
              Mirrors the upcoming-card pill (same locked copy from
              CQ4=a) — refunds take time to settle and the customer's
              later "did my refund ever come through?" check happens
              here, on PAST bookings. Testid uses `-past-` infix so the
              past and upcoming pills are independently greppable. */}
          {cancelled && b.payment_status === "paid" && (
            <p
              className="mt-2 text-xs italic text-muted-foreground"
              data-testid={`booking-refund-pending-past-${b.id}`}
            >
              Refund pending
            </p>
          )}
          {(b.payment_status === "refunded" ||
            b.payment_status === "partially_refunded") && (
            <p
              className="mt-2 text-xs font-medium text-pp-text"
              data-testid={`booking-refund-complete-past-${b.id}`}
            >
              Refunded ${((b.total_refunded_cents || 0) / 100).toFixed(2)}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total</p>
          <p className="font-heading text-xl font-bold text-pp-text">
            ${(b.pricing_snapshot?.total || 0).toFixed(2)}
          </p>
        </div>
      </div>

      {canReview && (
        <Button
          type="button"
          size="sm"
          onClick={() => onRate(b)}
          className="mt-3 rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid={`rate-my-pluck-btn-${b.id}`}
        >
          <Star className="mr-1 h-3.5 w-3.5" />
          Rate My Pluck
        </Button>
      )}

      <button
        type="button"
        onClick={onToggle}
        className="mt-3 ml-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
        data-testid={`booking-past-${b.id}-toggle-invoice`}
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? "Hide invoice" : "View invoice"}
      </button>
      {expanded && (
        <div
          className="mt-3 rounded-xl bg-muted/60 p-3"
          data-testid={`booking-past-${b.id}-invoice`}
        >
          <PriceBreakdown
            snapshot={b.pricing_snapshot}
            dogCount={b.dog_count}
            unsecuredCount={b.unsecured_count}
            compact
          />
        </div>
      )}
    </div>
  );
}

export default function CustomerBookingsPage() {
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [openInvoice, setOpenInvoice] = useState(null);
  const [rateTarget, setRateTarget] = useState(null);

  const pastSorted = useMemo(
    () => [...past].sort((a, b) => b.service_date.localeCompare(a.service_date)),
    [past]
  );

  const loadBookings = async () => {
    try {
      // Item E migration — only `/bookings/mine` is needed now. The
      // server-side `has_review` flag (Item #6) carries the
      // "is reviewed?" decision; the legacy parallel
      // `/reviews/mine` fetch + client-side join is gone. Two
      // upcoming/past calls (not one) is unchanged — it's existing
      // `upcoming=true|false` server-side filtering, not the join
      // bait the brief targeted.
      const [{ data: up }, { data: pa }] = await Promise.all([
        api.get("/bookings/mine", { params: { upcoming: true, view: "customer" } }),
        api.get("/bookings/mine", { params: { upcoming: false, view: "customer" } }),
      ]);
      setUpcoming(up);
      setPast(pa);
    } catch {
      /* swallow — non-critical: empty lists render fine. */
    }
  };

  useEffect(() => { loadBookings(); }, []);

  const { requestAction, gateProps } = useImpersonationGate();
  // Phase 3.6 — read PP_STRIPE_STUB state from the bootstrap context.
  // `stubMode === true` keeps the legacy "Pay now (mock)" button visible;
  // `false` hides it and shows a disabled-state note instead (real
  // payment-retry UX lands in a later phase).
  const { stubMode } = useStripeConfig();

  const handlePay = (id) => {
    requestAction("pay for this booking (mock)", async () => {
      try {
        await api.post(`/bookings/${id}/mock-pay`);
        toast.success("Paid (mocked).");
        loadBookings();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    });
  };

  const handleGenNext = (id) => {
    requestAction("generate next occurrence", async () => {
      try {
        await api.post(`/bookings/${id}/generate-next`);
        toast.success("Next occurrence created.");
        loadBookings();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    });
  };

  // Item E — refetch `/bookings/mine` after a review submission so the
  // server-side `has_review` flag flips to `true` and the badge
  // updates. No parallel client-side mirror; the server is the
  // source of truth.
  const handleReviewSubmitted = () => {
    loadBookings();
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="customer-bookings-page">
      <header className="mb-8">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Customer dashboard
        </span>
        <h1 className="font-heading text-4xl font-bold text-pp-text sm:text-5xl">
          My Plucks
        </h1>
        <p className="mt-2 text-muted-foreground">
          Your upcoming and past bookings, all in one place.
        </p>
      </header>

      <div className="flex items-center justify-end mb-4">
        <Button
          onClick={() => navigate("/book")}
          className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid="dashboard-book-now"
        >
          <Plus className="mr-1 h-4 w-4" /> Book a Pluck
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="rounded-3xl border-border bg-pp-surface p-6 shadow-none" data-testid="customer-upcoming-bookings">
          <div className="mb-3 flex items-center gap-2 text-pp-text">
            <CalendarClock className="h-5 w-5" />
            <h3 className="font-heading text-lg font-semibold">Future Plucks</h3>
          </div>
          {upcoming.length === 0 ? (
            <div className="rounded-2xl bg-secondary p-6 text-center">
              <PawPrint className="mx-auto h-8 w-8 text-brand-orange" />
              <p className="mt-2 text-sm text-muted-foreground">No Future Plucks yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((b) => (
                <div key={b.id} className="rounded-2xl border border-border p-4" data-testid={`booking-upcoming-${b.id}`}>
                  <div className="mb-1 flex items-center gap-2">
                    <BookingStatusPill status={b.status} />
                    <RecurringTag scheduleType={b.schedule_type} />
                  </div>
                  <p className="text-sm font-semibold text-pp-text">
                    {b.service_date} · {b.preferred_window.start}–{b.preferred_window.end}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Total ${b.pricing_snapshot.total.toFixed(2)}
                  </p>
                  {/* Phase 3.3 G3 — refund status pill (customer-visible).
                      Copy locked verbatim by dispatch CQ4=a — plain/clear,
                      deliberately NOT playful (money status needs trust).
                      Renders for cancelled + paid (pending refund) and
                      for refunded/partially_refunded (terminal). */}
                  {b.status === "cancelled" && b.payment_status === "paid" && (
                    <p
                      className="mt-2 text-xs italic text-muted-foreground"
                      data-testid={`booking-refund-pending-${b.id}`}
                    >
                      Refund pending
                    </p>
                  )}
                  {(b.payment_status === "refunded" ||
                    b.payment_status === "partially_refunded") && (
                    <p
                      className="mt-2 text-xs font-medium text-pp-text"
                      data-testid={`booking-refund-complete-${b.id}`}
                    >
                      Refunded $
                      {((b.total_refunded_cents || 0) / 100).toFixed(2)}
                    </p>
                  )}
                  {b.status === "payment_pending" && stubMode && (
                    <Button
                      size="sm"
                      onClick={() => handlePay(b.id)}
                      className="mt-2 rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                      data-testid={`booking-pay-${b.id}`}
                    >
                      Pay now (mock)
                    </Button>
                  )}
                  {b.status === "payment_pending" && !stubMode && (
                    <p
                      className="mt-2 text-xs italic text-muted-foreground"
                      data-testid={`booking-pay-disabled-note-${b.id}`}
                    >
                      Payment retry coming soon.
                    </p>
                  )}
                  {b.schedule_type !== "one_time" && (
                    <button
                      onClick={() => handleGenNext(b.id)}
                      className="ml-2 text-xs text-brand-orange hover:underline"
                      data-testid={`booking-gen-next-${b.id}`}
                      title="Use this if your last visit didn't auto-create the next one."
                    >
                      Generate next occurrence
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="rounded-3xl border-border bg-pp-surface p-6 shadow-none" data-testid="customer-past-bookings">
          <div className="mb-3 flex items-center gap-2 text-pp-text">
            <History className="h-5 w-5" />
            <h3 className="font-heading text-lg font-semibold">Past Plucks</h3>
          </div>
          {past.length === 0 ? (
            <div className="rounded-2xl bg-secondary p-6 text-center">
              <p className="text-sm text-muted-foreground">No Past Plucks yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pastSorted.map((b) => (
                <PastBookingCard
                  key={b.id}
                  b={b}
                  expanded={openInvoice === b.id}
                  onToggle={() =>
                    setOpenInvoice((cur) => (cur === b.id ? null : b.id))
                  }
                  onRate={setRateTarget}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      <ReviewModal
        open={!!rateTarget}
        onOpenChange={(open) => !open && setRateTarget(null)}
        booking={rateTarget}
        onSubmitted={handleReviewSubmitted}
      />
      <ImpersonationConfirmGate {...gateProps} />
    </div>
  );
}
